import React, { useState } from 'react';
import { Alert, Button, IconButton, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'contexts/AuthContext';
import AuthService from 'services/AuthService';

/**
 * Advance notice that the session is about to end.
 *
 * Deliberately *not* routed through `SessionExpirationHandler`: that component
 * logs the user out when it fires and auto-hides after five seconds, which is
 * right for "your session already ended" and exactly wrong for a warning about
 * one that has not. This one never ends a session and never hides on its own —
 * a warning the user has stepped away from is the case it exists for.
 *
 * The banner only appears for a session that could *not* be renewed silently
 * (`AuthContext` tries first), so by the time it shows, something needs the
 * user. It therefore carries an action: renew where that is possible, and
 * otherwise open the re-auth prompt — which, unlike the old advice to sign out
 * and back in, does not cost the user their unsaved work.
 */
const SessionExpiryWarning: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const {
    sessionExpiresAt,
    expiryWarningDismissed,
    dismissExpiryWarning,
    isAuthenticated,
    sessionLapsed,
    renewSessionNow,
    requestReauth,
  } = useAuth();
  const [busy, setBusy] = useState(false);

  /**
   * Is there anything left to extend?
   *
   * Past the backend's absolute cap no renewal will be granted, so offering
   * "extend" would be a lie — and a worse one than the old dead-end banner,
   * because it invites a click that cannot work. Null (a token minted before
   * `session_exp` existed) means no known cap: offer the action.
   */
  const absoluteEnd = AuthService.getAbsoluteSessionEnd();
  const canExtend =
    absoluteEnd === null || sessionExpiresAt === null || absoluteEnd > sessionExpiresAt;

  // Once the session has actually lapsed, `ReauthDialog` is the remedy and this
  // would only be a second voice saying the same thing.
  const open =
    isAuthenticated && !sessionLapsed && sessionExpiresAt !== null && !expiryWarningDismissed;

  /**
   * Renew, or fall through to the prompt.
   *
   * No cooldown here, deliberately: the automatic attempt behind this banner
   * has one, and a user who clicks *because* that attempt failed must not be
   * silently ignored.
   *
   * When renewal is not available at all — a password session, today every
   * session without SSO — the prompt takes over and signs the user in again
   * while the current token is still valid. That is a genuine extension, not a
   * rescue.
   */
  const handleExtend = async () => {
    setBusy(true);
    try {
      const renewed = await renewSessionNow();
      if (!renewed) requestReauth();
    } finally {
      setBusy(false);
    }
  };

  // A clock time ("ends at 17:40") is easier to act on than a countdown, and it
  // does not go stale between the 60s ticks that drive this.
  const time = sessionExpiresAt
    ? new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }).format(sessionExpiresAt)
    : null;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        severity="warning"
        onClose={dismissExpiryWarning}
        closeText={t('session.dismiss')}
        // MUI drops the built-in close button as soon as `action` is set, so
        // the dismiss control has to be rebuilt here — `onClose` alone would
        // silently lose it.
        action={
          <>
            {canExtend && (
              <Button color="inherit" size="small" onClick={handleExtend} disabled={busy}>
                {t('session.extend')}
              </Button>
            )}
            <IconButton
              size="small"
              color="inherit"
              onClick={dismissExpiryWarning}
              aria-label={t('session.dismiss')}
              title={t('session.dismiss')}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
        sx={{ width: '100%' }}
      >
        {canExtend
          ? (time ? t('session.expiringSoon', { time }) : t('session.expiringSoonShort'))
          : (time ? t('session.lastStretch', { time }) : t('session.expiringSoonShort'))}
      </Alert>
    </Snackbar>
  );
};

export default SessionExpiryWarning;
